import { Controller } from "@hotwired/stimulus"
import { usesSidebarLayout } from "../utils/layout_media"

// A Feed post's big buttons (its action, Discuss) start Jane on something in chat. On a desktop layout the
// conversation opens in Jane's right-hand panel, so the Feed stays where it is; on a phone, or where there is
// no panel to host it, the button goes to the chat page as before.
//
// The form posts as a turbo stream: the server answers with the post redrawn (done, "In chat") and names the
// conversation in X-Conversation-Id / X-Conversation-Url. Anything unexpected falls back to the plain form.
// Posts a person has folded in the moment: kept for this page's life only (the Feed redraws itself every half
// minute, which would otherwise unfold them), never saved.
const folded = (window.__feedFolded = window.__feedFolded || new Set())

export default class extends Controller {
  connect() {
    this.element.querySelectorAll("details.feed-post--open").forEach((details) => { if (folded.has(details.id)) details.open = false })
    this._onToggle = (event) => {
      const details = event.target
      if (!details.matches?.("details.feed-post--open")) return
      if (details.open) folded.delete(details.id)
      else folded.add(details.id)
    }
    this.element.addEventListener("toggle", this._onToggle, true)
  }

  disconnect() {
    this.element.removeEventListener("toggle", this._onToggle, true)
  }

  async submit(event) {
    const form = event.target.closest("form")
    if (form?.dataset.feedRsvp) return this._rsvp(event, form)
    if (!form || !form.dataset.feedAct || !usesSidebarLayout() || !document.querySelector('[data-controller~="jane-sidebar"]')) return

    event.preventDefault()
    const button = form.querySelector("button, input[type=submit]")
    if (button) button.disabled = true
    try {
      const response = await fetch(form.action, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: new FormData(form)
      })
      const conversationId = response.headers.get("X-Conversation-Id")
      if (!response.ok || !conversationId) throw new Error("no conversation")
      window.Turbo.renderStreamMessage(await response.text())
      // One panel at a time: a list or draft open beside the Feed steps aside for the conversation.
      document.dispatchEvent(new CustomEvent("jane:detail-sidebars-close", { detail: {} }))
      document.dispatchEvent(new CustomEvent("jane-sidebar:conversation", { detail: { conversationId } }))
    } catch (_error) {
      if (button) button.disabled = false
      form.submit()
    }
  }

  // Accept on a calendar invitation. Google only takes the answer from the person's own signed-in browser, so a
  // tab has to open; it is opened here, in the tap, and sent to Google's link once the server has it, while the
  // post closes in place on the Feed. If anything fails the plain form posts to a new tab as before.
  async _rsvp(event, form) {
    event.preventDefault()
    const button = form.querySelector("button, input[type=submit]")
    if (button) button.disabled = true
    const tab = window.open("", "_blank")
    try {
      const response = await fetch(form.action, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: new FormData(form)
      })
      const url = response.headers.get("X-Rsvp-Url")
      if (!response.ok || !url || !tab) throw new Error("no link")
      tab.location = url
      window.Turbo.renderStreamMessage(await response.text())
    } catch (_error) {
      tab?.close()
      if (button) button.disabled = false
      form.submit()
    }
  }
}
